/**
 * Infer scheduling / booking / platform signals from crawled website text.
 * All findings are labeled as inferred — never treated as confirmed integrations.
 */

export type InferredSchedulingSignal = {
  id: string;
  label: string;
  category: "platform" | "cta" | "method" | "portal" | "hours" | "social";
  inferred: true;
};

export type WebsiteSchedulingSignals = {
  /** Human-readable method guess (e.g. "Calendly", "Phone only"). */
  detectedSchedulingMethod: string | null;
  /** Platform name if a known scheduler/EMR string matched. */
  detectedPlatform: string | null;
  hasBookingCta: boolean;
  hasOnlineScheduling: boolean;
  phoneOnlyScheduling: boolean;
  hasPatientPortal: boolean;
  hasBusinessHours: boolean;
  appointmentFaqs: boolean;
  afterHoursMention: boolean;
  signals: InferredSchedulingSignal[];
};

const PLATFORM_PATTERNS: Array<{ id: string; label: string; terms: string[] }> = [
  { id: "calendly", label: "Calendly", terms: ["calendly.com", "calendly"] },
  { id: "jane", label: "Jane", terms: ["jane.app", "janeapp", "powered by jane"] },
  { id: "nexhealth", label: "NexHealth", terms: ["nexhealth", "nexhealth.com"] },
  { id: "juvonno", label: "Juvonno", terms: ["juvonno"] },
  {
    id: "google_calendar",
    label: "Google Calendar",
    terms: ["calendar.google.com", "google calendar", "googleapis.com/calendar"],
  },
  { id: "acuity", label: "Acuity", terms: ["acuityscheduling", "acuity scheduling"] },
  { id: "mindbody", label: "Mindbody", terms: ["mindbodyonline", "mindbody"] },
  { id: "square", label: "Square Appointments", terms: ["squareup.com/appointments", "square appointments"] },
  { id: "cliniko", label: "Cliniko", terms: ["cliniko"] },
  { id: "dentrix", label: "Dentrix", terms: ["dentrix"] },
  { id: "open_dental", label: "Open Dental", terms: ["opendental", "open dental"] },
  { id: "hubspot", label: "HubSpot", terms: ["hubspot.com/meetings", "meetings.hubspot"] },
  { id: "gohighlevel", label: "GoHighLevel", terms: ["gohighlevel", "highlevel"] },
];

const BOOKING_CTA_TERMS = [
  "book online",
  "book now",
  "book an appointment",
  "schedule online",
  "schedule an appointment",
  "request an appointment",
  "request appointment",
  "make an appointment",
  "online booking",
  "book a consultation",
  "schedule a visit",
];

const PORTAL_TERMS = [
  "patient portal",
  "client portal",
  "member login",
  "patient login",
  "mychart",
];

const HOURS_TERMS = ["hours of operation", "business hours", "office hours", "open monday", "mon-fri", "monday–friday"];

const AFTER_HOURS_TERMS = ["after hours", "after-hours", "24/7", "emergency line", "on-call"];

const APPOINTMENT_FAQ_TERMS = [
  "cancel appointment",
  "reschedule",
  "what to bring",
  "new patient forms",
  "insurance accepted",
];

function includesAny(lower: string, terms: string[]): boolean {
  return terms.some((t) => lower.includes(t));
}

/** Extract inferred scheduling signals from homepage / crawl text (labeled inferred). */
export function detectWebsiteSchedulingSignals(contentText: string): WebsiteSchedulingSignals {
  const lower = contentText.toLowerCase();
  const signals: InferredSchedulingSignal[] = [];

  let detectedPlatform: string | null = null;
  for (const p of PLATFORM_PATTERNS) {
    if (includesAny(lower, p.terms)) {
      detectedPlatform = p.label;
      signals.push({
        id: `platform-${p.id}`,
        label: `Possible ${p.label} integration`,
        category: "platform",
        inferred: true,
      });
      break;
    }
  }

  const hasBookingCta = includesAny(lower, BOOKING_CTA_TERMS);
  if (hasBookingCta) {
    signals.push({
      id: "cta-book",
      label: "Booking / schedule CTA on site",
      category: "cta",
      inferred: true,
    });
  }

  const hasOnlineScheduling =
    Boolean(detectedPlatform) ||
    hasBookingCta ||
    includesAny(lower, ["online scheduler", "appointment scheduling", "book your appointment"]);

  const hasPhone = includesAny(lower, ["call us", "call to schedule", "phone to book", "tel:"]);
  const phoneOnlyScheduling = hasPhone && !hasOnlineScheduling && !detectedPlatform;
  if (phoneOnlyScheduling) {
    signals.push({
      id: "method-phone",
      label: "Scheduling appears phone-driven",
      category: "method",
      inferred: true,
    });
  }

  const hasPatientPortal = includesAny(lower, PORTAL_TERMS);
  if (hasPatientPortal) {
    signals.push({
      id: "portal",
      label: "Patient / client portal link",
      category: "portal",
      inferred: true,
    });
  }

  const hasBusinessHours = includesAny(lower, HOURS_TERMS);
  if (hasBusinessHours) {
    signals.push({
      id: "hours",
      label: "Business hours listed",
      category: "hours",
      inferred: true,
    });
  }

  const appointmentFaqs = includesAny(lower, APPOINTMENT_FAQ_TERMS);
  if (appointmentFaqs) {
    signals.push({
      id: "faq-appt",
      label: "Appointment-related FAQ / copy",
      category: "cta",
      inferred: true,
    });
  }

  const afterHoursMention = includesAny(lower, AFTER_HOURS_TERMS);
  if (afterHoursMention) {
    signals.push({
      id: "after-hours",
      label: "After-hours demand signal",
      category: "hours",
      inferred: true,
    });
  }

  if (includesAny(lower, ["facebook.com", "instagram.com", "linkedin.com"])) {
    signals.push({
      id: "social",
      label: "Social profiles linked",
      category: "social",
      inferred: true,
    });
  }

  let detectedSchedulingMethod: string | null = null;
  if (detectedPlatform) detectedSchedulingMethod = detectedPlatform;
  else if (phoneOnlyScheduling) detectedSchedulingMethod = "Phone only";
  else if (hasOnlineScheduling) detectedSchedulingMethod = "Online booking (platform unknown)";
  else if (hasBookingCta) detectedSchedulingMethod = "Appointment request / CTA";

  return {
    detectedSchedulingMethod,
    detectedPlatform,
    hasBookingCta,
    hasOnlineScheduling,
    phoneOnlyScheduling,
    hasPatientPortal,
    hasBusinessHours,
    appointmentFaqs,
    afterHoursMention,
    signals,
  };
}
